/**
 * useMarketingDemoData — presentational demo objects for marketing/landing
 * pages. These feed the app's REAL components so the on-page "screenshots" are
 * always pixel-true to the current design. Nothing here is fetched or
 * persisted; they are not real clubs, players or results.
 */
export function useMarketingDemoData() {
  const demoTeam = (id, name, initials, extra = {}) => ({
    id,
    userId: id,
    name,
    initials,
    players: [],
    ...extra,
  });

  // Recent evenings, so the "Live matches" frame never shows stale dates.
  const playedAgo = (days, hour, minute = 0) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    date.setHours(hour, minute, 0, 0);
    return date.toISOString();
  };
  const mxFixture = (id, teamId, opponentTeamId, score, points, extra = {}) => ({
    id,
    teamId,
    opponentTeamId,
    homeTeamId: teamId,
    awayTeamId: opponentTeamId,
    score,
    points,
    ...extra,
  });

  /* ---- League division matrix (round-robin box) ---- */
  const leagueDivisionDemo = ref({
    id: 'demo-division',
    name: 'Division 1',
    clubId: 'demo-club',
    scoringSystem: {},
    teams: [
      demoTeam('dt1', 'Alex M.', 'AM', {
        position: 1, bonusPoints: 0, totalPoints: 6,
        fixtures: [
          mxFixture('demo-mx-13', 'dt1', 'dt3', '3 - 2', 3),
          mxFixture('demo-mx-12', 'dt1', 'dt2', '3 - 1', 3),
        ],
      }),
      demoTeam('dt3', 'Priya K.', 'PK', {
        position: 2, bonusPoints: 2, totalPoints: 5,
        fixtures: [
          mxFixture('demo-mx-13', 'dt3', 'dt1', '2 - 3', 2),
          mxFixture('demo-mx-23', 'dt3', 'dt2', '0 - 3', 0),
          mxFixture('demo-mx-34', 'dt3', 'dt4', '1 - 3', 1),
        ],
      }),
      demoTeam('dt2', 'Sam T.', 'ST', {
        position: 3, bonusPoints: 0, totalPoints: 4,
        fixtures: [
          mxFixture('demo-mx-12', 'dt2', 'dt1', '1 - 3', 1),
          mxFixture('demo-mx-23', 'dt2', 'dt3', '3 - 0', 3),
          mxFixture('demo-mx-24', 'dt2', 'dt4', '2 - 3', null, {requiresApprovalFrom: 'dt4'}),
        ],
      }),
      demoTeam('dt4', 'Jordan B.', 'JB', {
        position: 4, bonusPoints: 0, totalPoints: 3,
        fixtures: [
          mxFixture('demo-mx-34', 'dt4', 'dt3', '3 - 1', 3),
          mxFixture('demo-mx-24', 'dt4', 'dt2', '3 - 2', null, {requiresApprovalFrom: 'dt4'}),
        ],
      }),
    ],
  });

  /* ---- Ladder ---- */
  const ladderDemo = {
    topPlayersCount: 2,
    startDate: '2026-01-05T00:00:00Z',
    endDate: null,
    teams: [
      demoTeam('lt1', 'Dana R.', 'DR', {newPosition: 1, oldPosition: 1, incomingChallengeCount: 1}),
      demoTeam('lt2', 'Alex M.', 'AM', {newPosition: 2, oldPosition: 3, outgoingChallengeCount: 1}),
      demoTeam('lt3', 'Sam T.', 'ST', {newPosition: 3, oldPosition: 2}),
      demoTeam('lt4', 'Morgan L.', 'ML', {
        newPosition: 4, oldPosition: 4,
        frozen: true, frozenReason: 'Frozen while away. Keeps their position and cannot be challenged.',
      }),
      demoTeam('lt5', 'Priya K.', 'PK', {newPosition: 5, oldPosition: 5, incomingChallengeCount: 1, outgoingChallengeCount: 1}),
      demoTeam('lt6', 'Lee W.', 'LW', {newPosition: 6, oldPosition: 6}),
    ],
  };

  /* ---- Fixtures ---- */
  const heroFixtureDemo = {
    id: 'demo-fx-hero',
    homeTeamId: 'lt2',
    awayTeamId: 'lt3',
    homeTeam: demoTeam('lt2', 'Alex M.', 'AM'),
    awayTeam: demoTeam('lt3', 'Sam T.', 'ST'),
    homeTeamResult: 3,
    awayTeamResult: 1,
    playedAt: playedAgo(1, 18, 30),
    approvedAt: '2026-06-06T19:02:00Z',
    ladderId: 'demo-ladder',
    ladderName: 'Singles Ladder',
    ladderPositions: [
      {teamId: 'lt2', oldPosition: 3, newPosition: 2},
      {teamId: 'lt3', oldPosition: 2, newPosition: 3},
    ],
  };

  const playedFixtureDemo = {
    id: 'demo-fx-played',
    homeTeamId: 'ft1',
    awayTeamId: 'ft2',
    homeTeam: demoTeam('ft1', 'Chris D.', 'CD'),
    awayTeam: demoTeam('ft2', 'Lee W.', 'LW'),
    homeTeamResult: 3,
    awayTeamResult: 2,
    playedAt: playedAgo(3, 19),
    approvedAt: '2026-06-04T21:15:00Z',
    leagueId: 'demo-league',
    leagueCycleId: 'demo-cycle',
    leagueName: 'Club Championship',
    leagueCycleDivisionName: 'Division 1',
    games: [
      {homeTeamScore: 11, awayTeamScore: 8},
      {homeTeamScore: 7, awayTeamScore: 11},
      {homeTeamScore: 11, awayTeamScore: 9},
      {homeTeamScore: 9, awayTeamScore: 11},
      {homeTeamScore: 11, awayTeamScore: 6},
    ],
  };

  /* ---- Tournament bracket (semi-final with a bye, then the final) ---- */
  const bracketSemiDemo = {
    name: 'Semi-finals',
    teams: [
      demoTeam('bt1', 'Dana R.', 'DR', {isBye: true, bracketPosition: 1}),
      demoTeam('bt2', 'Sam T.', 'ST', {bracketPosition: 2}),
      demoTeam('bt3', 'Priya K.', 'PK', {bracketPosition: 3}),
    ],
    fixtures: [
      {
        id: 'demo-bk-semi',
        homeTeamId: 'bt2',
        awayTeamId: 'bt3',
        homeTeam: demoTeam('bt2', 'Sam T.', 'ST'),
        awayTeam: demoTeam('bt3', 'Priya K.', 'PK'),
        homeTeamResult: 3,
        awayTeamResult: 1,
        playedAt: playedAgo(0, 15),
        approvedAt: '2026-06-07T16:20:00Z',
        tournamentId: 'demo-tournament',
        tournamentStageId: 'demo-stage-semi',
        tournamentName: 'Summer Open',
        tournamentStageName: 'Semi-finals',
      },
    ],
  };

  const bracketFinalDemo = {
    name: 'Final',
    teams: [
      demoTeam('bt1', 'Dana R.', 'DR', {bracketPosition: 1}),
      demoTeam('bt2', 'Sam T.', 'ST', {bracketPosition: 2}),
    ],
    fixtures: [
      {
        id: 'demo-bk-final',
        homeTeamId: 'bt1',
        awayTeamId: 'bt2',
        homeTeam: demoTeam('bt1', 'Dana R.', 'DR'),
        awayTeam: demoTeam('bt2', 'Sam T.', 'ST'),
        scheduledAt: '2026-06-21T14:00:00Z',
        tournamentId: 'demo-tournament',
        tournamentStageId: 'demo-stage-final',
        tournamentName: 'Summer Open',
        tournamentStageName: 'Final',
      },
    ],
  };

  /* ---- Club stats (feeds the real StatsView component) ---- */
  const {doubleToPercentage, percentageToColor} = useStatConverters();

  const statPlayer = (id, fullName, initials) => ({id, fullName, initials, imageFileName: null});
  const sp = {
    dana: statPlayer('sp1', 'Dana Reed', 'DR'),
    alex: statPlayer('sp2', 'Alex Morgan', 'AM'),
    priya: statPlayer('sp3', 'Priya Khan', 'PK'),
    sam: statPlayer('sp4', 'Sam Taylor', 'ST'),
    lee: statPlayer('sp5', 'Lee Walker', 'LW'),
  };

  // Raw realtimeStats-shaped rows — StatsView derives the period/all-time form
  // and the sortable metric bars from these (same path as the live pages).
  // periods: {m1:[wins,total], m3, m6, m12}; all-time mirrors the 12-month run.
  const pct = (w, t) => (t ? w / t : 0);
  const rawStat = (player, score, p) => ({
    id: player.id,
    player,
    tapLeaguesScore: score,
    wins: p.m12[0], draws: 0, losses: p.m12[1] - p.m12[0], totalFixtures: p.m12[1],
    winPercentage: pct(p.m12[0], p.m12[1]),
    wins1Month: p.m1[0], total1Month: p.m1[1], winPercentage1Month: pct(p.m1[0], p.m1[1]),
    wins3Months: p.m3[0], total3Months: p.m3[1], winPercentage3Months: pct(p.m3[0], p.m3[1]),
    wins6Months: p.m6[0], total6Months: p.m6[1], winPercentage6Months: pct(p.m6[0], p.m6[1]),
    wins12Months: p.m12[0], total12Months: p.m12[1], winPercentage12Months: pct(p.m12[0], p.m12[1]),
  });

  const statsDemo = [
    rawStat(sp.dana, 1840, {m1: [4, 5], m3: [12, 14], m6: [22, 27], m12: [41, 52]}),
    rawStat(sp.alex, 1790, {m1: [5, 5], m3: [11, 14], m6: [21, 28], m12: [39, 53]}),
    rawStat(sp.priya, 1720, {m1: [3, 6], m3: [10, 14], m6: [18, 27], m12: [34, 51]}),
    rawStat(sp.sam, 1650, {m1: [3, 5], m3: [9, 14], m6: [16, 28], m12: [30, 52]}),
    rawStat(sp.lee, 1560, {m1: [2, 5], m3: [7, 13], m6: [13, 26], m12: [24, 49]}),
  ];

  /* ---- Americano division standings (per-player, sets/games tiebreaks) ---- */
  const americanoPlayer = (i, name, played, points, against, setsDiff, gamesDiff, position) => ({
    membershipId: `demo-am-m${i}`,
    playerId: `demo-am-p${i}`,
    fullName: name,
    firstName: name.split(' ')[0],
    lastName: name.split(' ')[1] ?? '',
    imageFileName: null,
    played,
    points,
    pointsAgainst: against,
    pointsDiff: points - against,
    setsDiff,
    gamesDiff,
    position,
    withdrawnAt: null,
  });
  const americanoStandingsDemo = {
    hasSetScores: true,
    players: [
      americanoPlayer(1, 'Sofia Marino', 3, 3, 0, 5, 14, 1),
      americanoPlayer(2, 'Jack Turner', 3, 2, 1, 2, 6, 2),
      americanoPlayer(3, 'Priya Patel', 3, 1, 2, -2, -7, 3),
      americanoPlayer(4, 'Leo Fernandez', 3, 0, 3, -5, -13, 4),
    ],
  };

  /* ---- Meet (recurring social session) — feeds MeetOccurrenceCard ---- */
  // The Mixer is on Wednesdays: tonight when it is a Wednesday before 19:00, else the next Wednesday, then the one after.
  const meetDemoIn = (weeks, hour) => {
    const date = new Date();
    const ahead = (3 - date.getDay() + 7) % 7;
    const tonightGone = ahead === 0 && date.getHours() >= hour;
    date.setDate(date.getDate() + (tonightGone ? 7 : ahead) + weeks * 7);
    date.setHours(hour, 0, 0, 0);
    return date.toISOString();
  };
  const meetDemo = {
    meet: {id: 'demo-meet', name: 'Wednesday Night Mixer', maxAttendees: 12},
    occurrences: [
      {
        id: 'demo-occ-1', startsAt: meetDemoIn(0, 19), endsAt: meetDemoIn(0, 21),
        attendingCount: 9, waitlistedCount: 0, rsvpIsOpen: true, status: 0,
      },
      {
        id: 'demo-occ-2', startsAt: meetDemoIn(1, 19), endsAt: meetDemoIn(1, 21),
        attendingCount: 12, waitlistedCount: 2, rsvpIsOpen: true, status: 0,
      },
    ],
    // AttendanceStatus.Attending — renders the "You're in" chip
    myAttendance: {status: 0},
  };

  return {
    leagueDivisionDemo,
    ladderDemo,
    americanoStandingsDemo,
    meetDemo,
    heroFixtureDemo,
    playedFixtureDemo,
    bracketSemiDemo,
    bracketFinalDemo,
    // stats (raw rows for the real StatsView component)
    statsDemo,
    doubleToPercentage,
    percentageToColor,
  };
}
